// Atölye kapı listesi (sadece isimler) ve imza listesi PDF'leri
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import moment from "moment";
import kongreLogoSvg from "./kongreLogoSvg";

const NAVY = rgb(0x21 / 255, 0x33 / 255, 0x6a / 255);
const GREY = rgb(0.45, 0.45, 0.45);
const LINE = rgb(0.75, 0.75, 0.75);
const BLACK = rgb(0, 0, 0);

const PAGE_W = 595.28; // A4
const PAGE_H = 841.89;
const MARGIN = 40;

const fontUrl = require("../../files/fonts/Roboto-Regular.ttf");

// SVG logoyu canvas ile PNG'ye çevirir (evenodd dolgusu doğru çıksın diye)
const logoToPng = () =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(new Blob([kongreLogoSvg], { type: "image/svg+xml" }));
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 1600;
      canvas.height = 524;
      canvas.getContext("2d").drawImage(img, 0, 0, 1600, 524);
      URL.revokeObjectURL(url);
      canvas.toBlob(async (b) => resolve(new Uint8Array(await b.arrayBuffer())), "image/png");
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Logo yüklenemedi")); };
    img.src = url;
  });

const timeText = (w) => {
  if (!w.start_at) return "";
  const s = moment(w.start_at);
  return s.format("DD.MM.YYYY HH:mm") + (w.end_at ? " – " + moment(w.end_at).format("HH:mm") : "");
};

/**
 * @param {Array} workshops  useWorkshopData satırları (title, moderator, room, start_at, end_at, people)
 * @param {"door"|"sign"} mode  door: sadece isimler, sign: ad + e-posta + imza
 */
export const buildWorkshopPdf = async (workshops, mode) => {
  const pdf = await PDFDocument.create();
  pdf.registerFontkit(fontkit);
  const font = await pdf.embedFont(await fetch(fontUrl).then((r) => r.arrayBuffer()), { subset: true });
  const logo = await pdf.embedPng(await logoToPng());

  const logoW = 150;
  const logoH = (logoW * 524) / 1600;

  const text = (page, str, x, y, size, color = BLACK) =>
    page.drawText(String(str ?? ""), { x, y, size, font, color });
  // Sığmayan metni kısalt
  const fit = (str, size, maxW) => {
    let s = String(str ?? "");
    if (font.widthOfTextAtSize(s, size) <= maxW) return s;
    while (s.length > 1 && font.widthOfTextAtSize(s + "…", size) > maxW) s = s.slice(0, -1);
    return s + "…";
  };

  // Sayfa başlığı; içerik başlangıç y'sini döner
  const drawHeader = (page, w, cont) => {
    let y = PAGE_H - MARGIN;
    page.drawImage(logo, { x: MARGIN, y: y - logoH, width: logoW, height: logoH });
    const title = mode === "door" ? "ATÖLYE KAPI LİSTESİ" : "ATÖLYE İMZA LİSTESİ";
    const tw = font.widthOfTextAtSize(title, 13);
    text(page, title, PAGE_W - MARGIN - tw, y - 18, 13, NAVY);
    y -= logoH + 18;

    const rows =
      mode === "door"
        ? [["Sınıf", w.room || "—"], ["Atölye", w.title], ["Saat", timeText(w) || "—"], ["Eğitmen", w.moderator || "—"]]
        : [["Sınıf", w.room || "—"], ["Atölye", w.title], ["Saat", timeText(w) || "—"], ["Eğitmen", w.moderator || "—"]];
    rows.forEach(([k, v]) => {
      text(page, k.toUpperCase(), MARGIN, y, 9, GREY);
      text(page, fit(v, mode === "door" ? 15 : 12, PAGE_W - MARGIN * 2 - 70), MARGIN + 70, y - (mode === "door" ? 2 : 0), mode === "door" ? 15 : 12, BLACK);
      y -= mode === "door" ? 24 : 19;
    });
    if (cont) text(page, "(devam)", PAGE_W - MARGIN - 45, y + 6, 9, GREY);
    y -= 4;
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 1, color: NAVY });
    return y - 8;
  };

  for (const w of workshops) {
    const people = w.people ?? [];
    let page = pdf.addPage([PAGE_W, PAGE_H]);
    let y = drawHeader(page, w, false);
    const bottom = MARGIN + 20;

    if (mode === "door") {
      // Sadece isimler — okunaklı, iri punto
      const rowH = 24;
      people.forEach((p, i) => {
        if (y - rowH < bottom) {
          page = pdf.addPage([PAGE_W, PAGE_H]);
          y = drawHeader(page, w, true);
        }
        y -= rowH;
        text(page, `${i + 1}.`, MARGIN, y + 6, 12, GREY);
        text(page, fit(p.name, 14, PAGE_W - MARGIN * 2 - 35), MARGIN + 35, y + 6, 14);
      });
      if (people.length === 0) text(page, "Katılımcı yok.", MARGIN, y - 20, 12, GREY);
    } else {
      // No | Ad Soyad | E-posta | İmza
      const colNo = MARGIN;
      const colName = MARGIN + 28;
      const colMail = MARGIN + 190;
      const colSign = MARGIN + 380;
      const right = PAGE_W - MARGIN;
      const rowH = 28;

      const drawTableHead = () => {
        y -= 16;
        text(page, "NO", colNo + 2, y + 4, 9, GREY);
        text(page, "AD SOYAD", colName, y + 4, 9, GREY);
        text(page, "E-POSTA", colMail, y + 4, 9, GREY);
        text(page, "İMZA", colSign, y + 4, 9, GREY);
        page.drawLine({ start: { x: MARGIN, y }, end: { x: right, y }, thickness: 0.5, color: LINE });
      };
      drawTableHead();

      people.forEach((p, i) => {
        if (y - rowH < bottom) {
          page = pdf.addPage([PAGE_W, PAGE_H]);
          y = drawHeader(page, w, true);
          drawTableHead();
        }
        y -= rowH;
        text(page, `${i + 1}`, colNo + 2, y + 10, 10, GREY);
        text(page, fit(p.name, 10.5, colMail - colName - 6), colName, y + 10, 10.5);
        text(page, fit(p.email, 8.5, colSign - colMail - 6), colMail, y + 10, 8.5, GREY);
        page.drawLine({ start: { x: MARGIN, y }, end: { x: right, y }, thickness: 0.5, color: LINE });
        // imza çizgisi
        page.drawLine({ start: { x: colSign, y: y + 6 }, end: { x: right, y: y + 6 }, thickness: 0.4, color: LINE });
      });
      if (people.length === 0) text(page, "Katılımcı yok.", MARGIN, y - 20, 12, GREY);
    }
  }

  return pdf.save();
};

export const downloadWorkshopPdf = async (workshops, mode, fileName) => {
  const bytes = await buildWorkshopPdf(workshops, mode);
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};
